import {
  Button,
  Column,
  Container,
  Copy,
  Flex,
  Heading,
  Image,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { getEntryById } from '@grubhubprod/umami-library/dist/contentful';
import {
  Breakpoint,
  useDeviceSize,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import classnames from 'classnames';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Transition, TransitionGroup } from 'react-transition-group';
import { LineOfCreditForm } from 'src/components/selfService/features/LineOfCredit/LineOfCreditForm';
import { ISignupStepProps } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTrackerStep/SignupProgressTrackerStep';
import {
  SignupStep,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import {
  DAYS,
  ILocFormFields,
  mapToFormValues,
  mapToPayload,
} from 'src/models/LineOfCreditModelUtils';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { sdk } from 'src/utilities/sdk';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { getWindow } from 'src/utilities/window';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { AnalyticsEventLabel } from 'src/types/Analytics';
import { ContentfulData } from 'src/types/LineOfCredit';
import { useGetQueryParams } from 'src/utilities/useGetQueryParams';

interface IContentfulResponse {
  data: ContentfulData[];
}

const contentfulEntryId = '6RYPhGR8RfsezzFYomK4FB';

const transitionDuration = 500;
const defaultLineOfCreditStepStyles = {
  transition: `all ${transitionDuration}ms ease-in`,
  opacity: 0,
};

const LineOfCreditStepTransitionStyles = {
  entering: {
    opacity: 0,
    height: 0,
  },
  entered: {
    opacity: 1,
    height: 'auto',
  },
  exiting: {
    opacity: 0,
  },
  exited: {
    opacity: 0,
  },
};

enum LineOfCreditStepPage {
  ONBOARDING,
  FORM,
}

const defaultDays = new Array(DAYS.length).fill(false);

const defaultFormValues = {
  creditName: '',
  amount: '',
  budgetLength: '',
  date: '',
  frequency: '',
  days: defaultDays,
};

export function LineOfCreditStep({
  updateMobileStepSize,
}: ISignupStepProps): JSX.Element {
  const { user } = useAuth();
  const [contentfulData, setContentfulData] = useState(null);
  const formMethods = useForm<ILocFormFields>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });
  const { formState, handleSubmit, watch } = formMethods;

  const selectedDays = watch('days', defaultDays);
  const frequency = watch('frequency');
  const budgetLength = watch('budgetLength', '');

  const { isValid } = formState;
  const {
    goToNextStep,
    goToPreviousStep,
    corpId,
    isStepCompleted,
    sendBackButtonAnalytics,
    isWelcomeBackModalPresent,
    isAdminIncludedInBudget,
  } = useSignupStatus();

  const isUpdating = useGetQueryParams('isUpdating');
  const [isUpdatingStep] = useState(isUpdating);
  const { breakpoint } = useDeviceSize(getWindow());
  const { startAsyncTask, status: submissionStatus } = useAsyncTask();

  const isCurrentStepCompleted = useRef(isStepCompleted(SignupStep.BUDGET));
  const [currentFormValues, setCurrentFormValues] =
    useState<ILocFormFields>(null);
  const [isExistingDataLoaded, setIsExistingDataLoaded] = useState(false);
  const defaultPage = isCurrentStepCompleted.current
    ? LineOfCreditStepPage.FORM
    : LineOfCreditStepPage.ONBOARDING;

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const existingLocId = useRef(null);

  const isMobile = breakpoint < Breakpoint.sm;

  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();

  function isFormValid(): boolean {
    let hasValidSelectedDays = true;

    if (budgetLength === 'day' && frequency === 'repeats') {
      hasValidSelectedDays = selectedDays.some((day) => day !== false);
    }

    return isValid && hasValidSelectedDays;
  }

  function shouldDisableSubmitButton(): boolean {
    if (currentPage === LineOfCreditStepPage.ONBOARDING) {
      return false;
    }

    // we want to keep the submission button disabled when the submission
    // is complete so no extra clicks happen before the next step renders
    return (
      !isFormValid() ||
      submissionStatus === AsyncTaskStatus.IN_PROGRESS ||
      submissionStatus === AsyncTaskStatus.COMPLETE
    );
  }

  useEffect(() => {
    async function getLineOfCreditDetails() {
      try {
        const { results } = await sdk.http.get(
          `/corps/${corpId}/eventmetadata/search?filter_by=ONLY_ACTIVE`
        );

        if (get(results, 'length') > 0) {
          const { id } = results[0];

          existingLocId.current = id;

          setCurrentFormValues({
            ...mapToFormValues(results[0]),
            isAdminIncludedInBudget,
          });
        }
        setIsExistingDataLoaded(true);
      } catch (error) {
        pushErrorFromAPI(error);
      }
    }
    if (
      corpId &&
      isCurrentStepCompleted.current &&
      currentPage === LineOfCreditStepPage.FORM
    ) {
      getLineOfCreditDetails();
    }
  }, [currentPage, corpId, isAdminIncludedInBudget]);

  useEffect(() => {
    // send analytics on each page change between Onboard and Form
    if (!isWelcomeBackModalPresent) {
      sendTealiumPageView({
        pageGroup: 'corporate self service - sign up flow',
        pageName:
          currentPage === LineOfCreditStepPage.ONBOARDING
            ? 'line of credit - onboarding'
            : 'line of credit - creation',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isWelcomeBackModalPresent]);

  useEffect(() => {
    async function getContentfulData() {
      try {
        const response: IContentfulResponse = await getEntryById(
          contentfulEntryId,
          {
            include: 2,
          }
        );

        const data = get(response, 'data', 0);

        if (data) {
          setContentfulData(data);
          if (updateMobileStepSize) {
            updateMobileStepSize();
          }
        }
      } catch (error) {
        // TODO do something else?
        // eslint-disable-next-line no-console
        console.warn(error);
      }
    }

    getContentfulData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (submissionStatus === AsyncTaskStatus.COMPLETE) {
      goToNextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionStatus]);

  const isOnboardingPage = currentPage === LineOfCreditStepPage.ONBOARDING;

  function onContinueButtonClick(values: ILocFormFields): void {
    if (isOnboardingPage) {
      setCurrentPage(LineOfCreditStepPage.FORM);
    } else {
      submitForm(values);
    }
  }

  async function submitForm(values: ILocFormFields) {
    const budgetPayload = mapToPayload(values);
    try {
      if (existingLocId.current) {
        await startAsyncTask(() => {
          return sdk.http.put(
            `/corp-admin/${corpId}/budget/${existingLocId.current}`,
            budgetPayload
          );
        });
      } else {
        await startAsyncTask(() => {
          return sdk.http.post(`/corp-admin/${corpId}/budget`, budgetPayload);
        });
      }
      // Update basic account to indicate whether or not admin is included in budget
      await sdk.http.put(
        `/corp-admin/corp/self/management/${user.ud_id}/${corpId}`,
        {
          admin_included_in_budget_on_sign_up_complete:
            values.isAdminIncludedInBudget,
        }
      );
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  function onSkip(): void {
    sendTealiumEvent({
      eventcategory: 'self-service sign up_line of credit',
      eventaction: 'line_of_credit_skip',
      eventlabel: `skip_${AnalyticsEventLabel.SUCCESS}`,
    });
    goToNextStep();
  }

  function onBackButtonClick(): void {
    sendBackButtonAnalytics();
    if (isOnboardingPage) {
      goToPreviousStep();
    } else {
      setCurrentPage(LineOfCreditStepPage.ONBOARDING);
    }
  }

  function getPrimaryButtonText(): JSX.Element {
    if (isOnboardingPage) {
      return <span>Create meal credit</span>;
    }

    if (isUpdatingStep) {
      return <span>Update</span>;
    }

    return <span>Next</span>;
  }

  if (isOnboardingPage && !contentfulData) {
    return null;
  }

  return (
    <>
      <Container variant="sm">
        <TransitionGroup>
          {isOnboardingPage && (
            <Transition timeout={transitionDuration}>
              {(transitionState) => (
                <OnboardingSection
                  style={{
                    ...defaultLineOfCreditStepStyles,
                    ...LineOfCreditStepTransitionStyles[transitionState],
                  }}
                  contentfulData={contentfulData}
                  isMobile={isMobile}
                />
              )}
            </Transition>
          )}
          {!isOnboardingPage && (
            <Transition timeout={transitionDuration}>
              {(transitionState) => (
                <LineOfCreditForm
                  updateMobileStepSize={updateMobileStepSize}
                  isEditing={!!existingLocId.current}
                  useDatePickerModal={isMobile}
                  formMethods={formMethods}
                  isDataLoading={
                    isCurrentStepCompleted.current && !isExistingDataLoaded
                  }
                  values={currentFormValues}
                  defaultDays={defaultDays}
                  style={{
                    ...defaultLineOfCreditStepStyles,
                    ...LineOfCreditStepTransitionStyles[transitionState],
                  }}
                  isSignup
                />
              )}
            </Transition>
          )}
        </TransitionGroup>
      </Container>
      <SignupStepFooter>
        <Flex justifyContent={isUpdatingStep ? 'flex-end' : 'space-between'}>
          {!isUpdatingStep && (
            <Stack
              x={isMobile ? 'x-sm' : 'none'}
              width={isMobile ? '100%' : 'auto'}
            >
              <Button
                variant="secondary"
                buttonSize="medium"
                block={isMobile}
                onClick={onBackButtonClick}
              >
                Back
              </Button>
            </Stack>
          )}
          {!isUpdatingStep && (
            <Button
              variant="tertiary"
              buttonSize="medium"
              block={isMobile}
              onClick={onSkip}
            >
              Skip for now
            </Button>
          )}
          <Button
            variant="primary"
            buttonSize="medium"
            block={isMobile}
            disabled={shouldDisableSubmitButton()}
            isLoading={submissionStatus === AsyncTaskStatus.IN_PROGRESS}
            onClick={handleSubmit(onContinueButtonClick)}
          >
            {getPrimaryButtonText()}
          </Button>
        </Flex>
      </SignupStepFooter>
    </>
  );
}

interface IOnboardingSectionProps {
  contentfulData: ContentfulData;
  isMobile: boolean;
  style: CSSProperties;
}

function OnboardingSection({
  contentfulData,
  isMobile,
  style,
}: IOnboardingSectionProps): JSX.Element {
  const {
    staticwebThreeinfographPrimaryText,
    staticwebThreeinfographTychs = [],
  } = contentfulData;

  return (
    <div style={style}>
      <Stack y="sm">
        <Container variant="xs">
          <Heading variant="h1" textAlign={isMobile ? 'left' : 'center'}>
            {staticwebThreeinfographPrimaryText}
          </Heading>
        </Container>
      </Stack>
      <Row gutter={0}>
        {staticwebThreeinfographTychs.map((tych, index) => (
          <Column key={tych.id} tablet={4} gutter={24}>
            <Tych
              tych={tych}
              isLastTych={index === staticwebThreeinfographTychs.length - 1}
            />
          </Column>
        ))}
      </Row>
    </div>
  );
}

function Tych({ tych, isLastTych }) {
  const { breakpoint } = useDeviceSize(getWindow());

  const isPhone = breakpoint <= Breakpoint.sm;

  const {
    staticwebTychImageAltText,
    staticwebTychImageUrl,
    staticwebTychPrimaryText,
    staticwebTychSecondaryText,
  } = tych;

  return (
    <Stack y={isPhone && !isLastTych ? 'lg' : 'none'}>
      <Flex
        flexDirection={isPhone ? 'row' : 'column'}
        alignItems={isPhone ? 'flex-start' : 'center'}
      >
        <Stack y="sm" x={isPhone ? 'md' : 'none'} flexShrink={isPhone ? 0 : 1}>
          <Image
            width={isPhone ? '60px' : '100%'}
            src={staticwebTychImageUrl}
            alt={staticwebTychImageAltText}
          />
        </Stack>
        <div
          className={classnames('lineOfCreditStep_textContainer', {
            mobile: isPhone,
          })}
        >
          <Stack y={isPhone ? 'md' : 'x-sm'}>
            <Heading variant="h5" textAlign={isPhone ? 'left' : 'center'}>
              {staticwebTychPrimaryText}
            </Heading>
          </Stack>
          <Copy variant="body" textAlign={isPhone ? 'left' : 'center'}>
            {staticwebTychSecondaryText}
          </Copy>
        </div>
      </Flex>
    </Stack>
  );
}
